import i18n from "../i18n";

/**
 * Language helpers.
 *
 * The active language is kept by i18next and mirrored in localStorage under
 * I18N_LANGUAGE ("eng" | "arab"). Everything here reads i18n first and falls
 * back to localStorage so it also works outside React components (api layer,
 * sweetalert wrapper, ...).
 */

export const DEFAULT_LANGUAGE = "eng";

// i18n language key -> ISO code sent to the backend in Accept-Language
const ISO_CODES = {
  eng: "en",
  arab: "ar",
};

const RTL_LANGUAGES = ["arab"];

// Eastern Arabic-Indic digits, used for numbers when Arabic is selected.
const ARABIC_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

export const getCurrentLanguage = () => {
  const language =
    (i18n && i18n.language) ||
    localStorage.getItem("I18N_LANGUAGE") ||
    DEFAULT_LANGUAGE;
  // guard against detected codes such as "en-US"
  return ISO_CODES[language] ? language : DEFAULT_LANGUAGE;
};

export const getLanguageCode = () => ISO_CODES[getCurrentLanguage()] || "en";

export const isRTL = () => RTL_LANGUAGES.indexOf(getCurrentLanguage()) !== -1;

export const getDirection = () => (isRTL() ? "rtl" : "ltr");

/**
 * Translate a key using the active language.
 *
 * nsSeparator/keySeparator are disabled per call so free text containing ":"
 * or "." (API messages) is never mistaken for a namespace or a nested key.
 */
export const translate = (key, options = {}) => {
  if (key === null || key === undefined || key === "") return key;
  return i18n.t(key, {
    defaultValue: key,
    nsSeparator: false,
    keySeparator: false,
    ...options,
  });
};

const hasTranslation = (key) => {
  if (!key) return false;
  try {
    return i18n.exists(key, { nsSeparator: false, keySeparator: false });
  } catch (e) {
    return false;
  }
};

/**
 * Locale aware number formatting for values coming from the API (dashboard
 * totals, sidebar notification badges, ...).
 *
 * These values are dynamic: the API may send them as a number (177), as a
 * string ("177"), already grouped ("1,234"), or with a suffix ("99+"). The
 * function therefore only substitutes the DIGIT CHARACTERS and leaves every
 * other character in place, so nothing is ever lost or reinterpreted:
 *
 *   eng : 177 -> "177"   | "1,234" -> "1,234" | "99+" -> "99+"
 *   arab: 177 -> "١٧٧"   | "1,234" -> "١,٢٣٤" | "99+" -> "٩٩+"
 *
 * In English the output is always exactly what React would have rendered
 * before, so switching to this helper cannot change the English UI.
 */
/**
 * Walk every text node under `root` and swap Western digits for Eastern
 * Arabic-Indic digits when Arabic is selected. No-op (and safe to call
 * repeatedly) in English, and a no-op once digits are already converted -
 * digit-only substitution, nothing else about the text is touched.
 *
 * Used to localize numbers rendered by third-party components (e.g. the
 * "1-4 of 4" / rows-per-page text react-data-table-component renders
 * internally) where there's no prop to run our own formatter over the text.
 */
export const localizeDigitsInElement = (root) => {
  if (!root || !isRTL() || typeof document === "undefined") return;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes = [];
  let node = walker.nextNode();
  while (node) {
    nodes.push(node);
    node = walker.nextNode();
  }

  nodes.forEach((textNode) => {
    if (/[0-9]/.test(textNode.nodeValue)) {
      textNode.nodeValue = textNode.nodeValue.replace(
        /[0-9]/g,
        (digit) => ARABIC_DIGITS[Number(digit)]
      );
    }
  });
};

export const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return value;

  // only primitives are safe to stringify; anything else is returned as-is
  // so it can never render as "[object Object]"
  const type = typeof value;
  if (type !== "number" && type !== "string" && type !== "bigint") return value;

  // NaN / Infinity would render as "NaN" / "Infinity" - show nothing instead
  if (type === "number" && !Number.isFinite(value)) return "";

  const text = String(value);
  if (!isRTL()) return text;

  return text.replace(/[0-9]/g, (digit) => ARABIC_DIGITS[Number(digit)]);
};

/**
 * formatNumber for counts. Identical to formatNumber except that a missing
 * value renders as an empty string rather than leaking null/undefined into
 * the markup. Deliberately does NOT coerce with Number(): a value the API
 * sends as "1,234" must keep its digits rather than collapse to NaN.
 */
export const formatCount = (value) => {
  if (value === null || value === undefined || value === "") return "";

  // a count must always render as text; anything that is not a number or a
  // string (object, array, boolean) becomes "" so React is never handed a
  // value it cannot render
  const type = typeof value;
  if (type !== "number" && type !== "string" && type !== "bigint") return "";

  const formatted = formatNumber(value);
  return formatted === null || formatted === undefined ? "" : String(formatted);
};

/**
 * Laravel style validation messages returned by the API.
 *
 * The backend sends the resolved sentence ("The image must be an image.")
 * rather than the template, so each known template is matched back with a
 * regexp, the attribute name is translated on its own, and the localised
 * sentence is rebuilt through i18next interpolation.
 */
const API_MESSAGE_PATTERNS = [
  {
    regex: /^the (.+?) field is required\.?$/i,
    key: "The {{attribute}} field is required.",
  },
  {
    regex: /^the (.+?) is required\.?$/i,
    key: "The {{attribute}} is required.",
  },
  {
    regex: /^the (.+?) must be an image\.?$/i,
    key: "The {{attribute}} must be an image.",
  },
  {
    regex: /^the (.+?) must be a file of type: (.+?)\.?$/i,
    key: "The {{attribute}} must be a file of type: {{values}}.",
    groups: ["attribute", "values"],
  },
  {
    regex: /^the (.+?) has already been taken\.?$/i,
    key: "The {{attribute}} has already been taken.",
  },
  {
    regex: /^the (.+?) must be a valid email address\.?$/i,
    key: "The {{attribute}} must be a valid email address.",
  },
  {
    regex: /^the (.+?) must be a number\.?$/i,
    key: "The {{attribute}} must be a number.",
  },
  {
    regex: /^the (.+?) must be a string\.?$/i,
    key: "The {{attribute}} must be a string.",
  },
  {
    regex: /^the (.+?) must be a date after (.+?)\.?$/i,
    key: "The {{attribute}} must be a date after {{date}}.",
    groups: ["attribute", "date"],
  },
  {
    regex: /^the (.+?) must not be greater than (.+?)\.?$/i,
    key: "The {{attribute}} must not be greater than {{max}}.",
    groups: ["attribute", "max"],
  },
  {
    regex: /^the (.+?) may not be greater than (.+?)\.?$/i,
    key: "The {{attribute}} must not be greater than {{max}}.",
    groups: ["attribute", "max"],
  },
  {
    regex: /^the (.+?) must be at least (.+?)\.?$/i,
    key: "The {{attribute}} must be at least {{min}}.",
    groups: ["attribute", "min"],
  },
  {
    regex: /^the (.+?) format is invalid\.?$/i,
    key: "The {{attribute}} format is invalid.",
  },
  {
    regex: /^the (.+?) confirmation does not match\.?$/i,
    key: "The {{attribute}} confirmation does not match.",
  },
  {
    regex: /^the selected (.+?) is invalid\.?$/i,
    key: "The selected {{attribute}} is invalid.",
  },

  // ---------------------------------------------------------------
  // CRUD result sentences ("Category updated successfully").
  // requireKnown: the sentence is only rebuilt when the resource noun
  // itself has a translation, so a half English / half Arabic sentence
  // is never produced - the original message is shown instead.
  // ---------------------------------------------------------------
  {
    regex: /^(.+?) status (?:has been )?updated successfully\.?$/i,
    key: "{{resource}} status updated successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?created successfully\.?$/i,
    key: "{{resource}} created successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?updated successfully\.?$/i,
    key: "{{resource}} updated successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?deleted successfully\.?$/i,
    key: "{{resource}} deleted successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?added successfully\.?$/i,
    key: "{{resource}} added successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?saved successfully\.?$/i,
    key: "{{resource}} saved successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?removed successfully\.?$/i,
    key: "{{resource}} removed successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?(?:retrieved|fetched|listed) successfully\.?$/i,
    key: "{{resource}} retrieved successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?approved successfully\.?$/i,
    key: "{{resource}} approved successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) (?:has been |was )?rejected successfully\.?$/i,
    key: "{{resource}} rejected successfully",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) not found\.?$/i,
    key: "{{resource}} not found",
    groups: ["resource"],
    requireKnown: true,
  },
  {
    regex: /^(.+?) already exists\.?$/i,
    key: "{{resource}} already exists",
    groups: ["resource"],
    requireKnown: true,
  },
];

const translateAttribute = (attribute) => {
  if (!attribute) return attribute;
  const trimmed = attribute.trim();
  if (hasTranslation(trimmed)) return translate(trimmed);

  // "profile_image" / "profile image" -> "Profile Image"
  const readable = trimmed.replace(/_/g, " ");
  const titleCased = readable.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  if (hasTranslation(titleCased)) return translate(titleCased);
  if (hasTranslation(readable)) return translate(readable);

  return readable;
};

/** true when the noun itself resolves to something in the dictionary */
const isKnownNoun = (value) => {
  if (!value) return false;
  const trimmed = value.trim();
  const readable = trimmed.replace(/_/g, " ");
  const titleCased = readable.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  return (
    hasTranslation(trimmed) ||
    hasTranslation(titleCased) ||
    hasTranslation(readable)
  );
};

const translateByPattern = (message) => {
  for (let i = 0; i < API_MESSAGE_PATTERNS.length; i += 1) {
    const pattern = API_MESSAGE_PATTERNS[i];
    const match = message.match(pattern.regex);
    if (!match) continue;
    if (!hasTranslation(pattern.key)) continue;

    const groups = pattern.groups || ["attribute"];

    // never build a half translated sentence
    if (pattern.requireKnown && !isKnownNoun(match[1])) continue;

    const values = {};
    groups.forEach((name, index) => {
      const value = match[index + 1];
      values[name] =
        name === "attribute" || name === "resource"
          ? translateAttribute(value)
          : formatNumber(value);
    });

    return translate(pattern.key, values);
  }
  return null;
};

/**
 * Translate a label that came from the API (sidebar menu labels, ...).
 *
 * The backend is not guaranteed to send the label exactly as it is spelled in
 * the translation files, so trailing whitespace, double spaces and casing are
 * all tolerated before giving up and returning the original label.
 */
export const translateLabel = (label) => {
  if (typeof label !== "string" || !label.trim()) return label;

  if (hasTranslation(label)) return translate(label);

  const trimmed = label.trim();
  if (hasTranslation(trimmed)) return translate(trimmed);

  const collapsed = trimmed.replace(/\s+/g, " ");
  if (hasTranslation(collapsed)) return translate(collapsed);

  // case insensitive match against the loaded resource bundle
  const bundle =
    (i18n.getResourceBundle &&
      i18n.getResourceBundle(getCurrentLanguage(), "translation")) ||
    null;
  if (bundle) {
    const wanted = collapsed.toLowerCase();
    const keys = Object.keys(bundle);
    for (let i = 0; i < keys.length; i += 1) {
      if (keys[i].trim().replace(/\s+/g, " ").toLowerCase() === wanted) {
        return bundle[keys[i]];
      }
    }
  }

  return label;
};

/**
 * Translate a message produced by the API (or any free text) into the active
 * language. Falls back to the original text whenever no translation exists, so
 * unknown backend messages are still shown to the user unchanged.
 */
export const translateMessage = (message) => {
  if (message === null || message === undefined) return message;

  // Laravel validation bags: { field: ["msg", ...] }
  if (Array.isArray(message)) {
    return message.map((item) => translateMessage(item)).join("\n");
  }

  if (typeof message === "object") {
    return Object.keys(message)
      .map((field) => translateMessage(message[field]))
      .join("\n");
  }

  if (typeof message !== "string") return message;

  const raw = message.trim();
  if (!raw) return message;

  // 1. exact match
  if (hasTranslation(raw)) return translate(raw);

  // 2. same message without the trailing period
  const withoutPeriod = raw.replace(/\.+$/, "");
  if (withoutPeriod !== raw && hasTranslation(withoutPeriod)) {
    return translate(withoutPeriod);
  }

  // 3. known Laravel validation sentence
  const byPattern = translateByPattern(raw);
  if (byPattern) return byPattern;

  // 4. unknown message - show it exactly as the API sent it
  return message;
};

/**
 * Pull a displayable message out of an axios error / response payload and
 * translate it. `fallback` is translated too when nothing usable is found.
 */
export const getApiMessage = (source, fallback = "Something went wrong") => {
  const payload =
    (source && source.response && source.response.data) ||
    (source && source.data) ||
    source;

  const message =
    (payload && (payload.message || payload.error)) ||
    (payload && payload.errors) ||
    null;

  if (!message) return translate(fallback);
  return translateMessage(message);
};

export default {
  DEFAULT_LANGUAGE,
  getCurrentLanguage,
  getLanguageCode,
  isRTL,
  getDirection,
  translate,
  translateLabel,
  translateMessage,
  getApiMessage,
  formatNumber,
  formatCount,
};
